/**
 * BriefChatPanel — message thread + composer for brief chat.
 *
 * Two modes:
 *   - default: full-pane (380px wide, used by brief-share SPA's left rail)
 *   - compact: header-less, no quota badges, used inside a sidecar that
 *     renders its own header and chip rail.
 */

import { useEffect, useRef } from 'react';
import type { ChatMessage } from '../hooks/useBriefChat';
import type { ChatMention } from './ChatComposer';
import type { MentionItem } from './MentionDropdown';
import { ChatComposer } from './ChatComposer';
import { CardRenderer } from './CardRenderer';
import type { ProductRecommendationRow } from './RecommendationRow';
import { AgentThinkingIndicator, StreamingCursor } from './AgentThinkingIndicator';

interface BriefChatPanelProps {
  messages: ChatMessage[];
  isStreaming: boolean;
  error?: string | null;
  editsRemaining?: number | null;
  generationsRemaining?: number | null;
  activeSections: string[];
  activeAngles: string[];
  onSend: (text: string, mentions?: ChatMention[]) => void;
  onUnpin: (kind: 'section' | 'angle', id: string) => void;
  canChat?: boolean;
  permissionHint?: string;
  mentionItems?: MentionItem[];
  onOpenAssetTray?: () => void;
  attachmentCount?: number;
  suggestionRail?: React.ReactNode;
  prefill?: string;
  onPrefillConsumed?: () => void;
  /** When true, omits the header and quota badges (sidecar host renders its own). */
  compact?: boolean;
  /** Override the outer container's className (full-pane defaults to a 380px aside). */
  className?: string;
  onApplyProducts?: (recommendations: ProductRecommendationRow[]) => void | Promise<void>;
}

export function BriefChatPanel({
  messages,
  isStreaming,
  error,
  editsRemaining,
  generationsRemaining,
  activeSections,
  activeAngles,
  onSend,
  onUnpin,
  canChat = true,
  permissionHint,
  mentionItems,
  onOpenAssetTray,
  attachmentCount = 0,
  suggestionRail,
  prefill,
  onPrefillConsumed,
  compact = false,
  className,
  onApplyProducts,
}: BriefChatPanelProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, isStreaming]);

  const containerClass =
    className ??
    (compact
      ? 'flex flex-col h-full bg-white overflow-hidden'
      : 'w-[380px] shrink-0 border-r border-slate-200 bg-white flex flex-col h-full');

  return (
    <aside className={containerClass}>
      {!compact && (
        <header className="px-4 py-3 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-bold text-slate-900">Brief Studio Chat</h2>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Ask questions, patch sections, or run a generation.
              </p>
            </div>
            {onOpenAssetTray && (
              <button
                type="button"
                onClick={onOpenAssetTray}
                title="Attachments"
                className="relative flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-800 transition px-2 py-1 rounded hover:bg-slate-100"
              >
                <span className="text-base">{'\u{1F4CE}'}</span>
                {attachmentCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-brand-purple text-white text-[8px] flex items-center justify-center font-bold">
                    {attachmentCount > 9 ? '9+' : attachmentCount}
                  </span>
                )}
              </button>
            )}
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5 text-[10px]">
            <QuotaBadge label="Edits" value={editsRemaining ?? null} tone="purple" />
            <QuotaBadge label="Generations" value={generationsRemaining ?? null} tone="teal" />
          </div>
        </header>
      )}

      {(activeSections.length > 0 || activeAngles.length > 0) && (
        <div className="px-4 py-2 border-b border-slate-100 bg-slate-50/60">
          <div className="text-[9px] uppercase tracking-wide text-slate-400 mb-1">
            Pinned to next message
          </div>
          <div className="flex flex-wrap gap-1">
            {activeSections.map((s) => (
              <PinnedChip
                key={`s-${s}`}
                label={s.replace(/_/g, ' ')}
                tone="purple"
                onRemove={() => onUnpin('section', s)}
              />
            ))}
            {activeAngles.map((a) => (
              <PinnedChip
                key={`a-${a}`}
                label={`angle ${a}`}
                tone="teal"
                onRemove={() => onUnpin('angle', a)}
              />
            ))}
          </div>
        </div>
      )}

      {suggestionRail}

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
        {messages.length === 0 && !isStreaming && (
          <div className="text-[11px] text-slate-400 italic">
            No messages yet — say hi or pick a suggestion above.
          </div>
        )}
        {messages.map((m, i) => {
          const isLastMsg = i === messages.length - 1;
          const isLastAssistant = isLastMsg && m.role === 'assistant';
          // Treat whitespace-only content as "still thinking" — the first SSE
          // chunk is often a newline/space before real content arrives.
          const hasVisibleContent = !!m.content?.trim();
          // Show neural indicator when waiting for first real token
          const isThinking = isStreaming && isLastAssistant && !hasVisibleContent;
          // Show streaming cursor while content is actively flowing in
          const showCursor = isStreaming && isLastAssistant && hasVisibleContent;
          return (
            <MessageView
              key={m.id}
              message={m}
              isThinking={isThinking}
              showCursor={showCursor}
              onApplyProducts={onApplyProducts}
            />
          );
        })}
        {error && (
          <div className="text-[11px] text-rose-600 bg-rose-50 border border-rose-200 rounded px-2 py-1.5">
            {error}
          </div>
        )}
      </div>

      {canChat ? (
        <ChatComposer
          disabled={isStreaming}
          onSend={onSend}
          mentionItems={mentionItems}
          prefill={prefill}
          onPrefillConsumed={onPrefillConsumed}
        />
      ) : (
        <div className="p-3 border-t border-slate-200 bg-slate-50 text-[11px] text-slate-500">
          {permissionHint || 'Chat is disabled for this share link.'}
        </div>
      )}
    </aside>
  );
}

function QuotaBadge({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | null;
  tone: 'purple' | 'teal';
}) {
  if (value == null) {
    return (
      <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-400 border border-slate-200">
        {label}: —
      </span>
    );
  }
  const cls =
    value <= 0
      ? 'bg-rose-100 text-rose-700 border-rose-200'
      : value <= 3
        ? 'bg-amber-100 text-amber-700 border-amber-200'
        : tone === 'purple'
          ? 'bg-brand-purple/10 text-indigo-700 border-indigo-200'
          : 'bg-brand-teal/10 text-teal-700 border-teal-200';
  return (
    <span className={`px-1.5 py-0.5 rounded border ${cls}`}>
      {label}: {value}
    </span>
  );
}

function PinnedChip({
  label,
  tone,
  onRemove,
}: {
  label: string;
  tone: 'purple' | 'teal';
  onRemove: () => void;
}) {
  const cls =
    tone === 'purple'
      ? 'bg-brand-purple/10 text-indigo-700 border-indigo-200'
      : 'bg-brand-teal/10 text-teal-700 border-teal-200';
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border ${cls}`}
    >
      {label}
      <button
        onClick={onRemove}
        className="hover:text-rose-500 transition"
        title="Unpin"
      >
        ×
      </button>
    </span>
  );
}

function MessageView({
  message,
  isThinking = false,
  showCursor = false,
  onApplyProducts,
}: {
  message: ChatMessage;
  isThinking?: boolean;
  showCursor?: boolean;
  onApplyProducts?: (recommendations: ProductRecommendationRow[]) => void | Promise<void>;
}) {
  if (message.role === 'system') {
    return (
      <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-100 rounded px-3 py-2">
        {message.content}
      </div>
    );
  }
  const isUser = message.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`${
          isUser
            ? 'bg-brand-purple text-white'
            : 'bg-slate-100 text-slate-800'
        } rounded-2xl px-3 py-2 max-w-[88%] text-sm leading-snug shadow-sm`}
      >
        {/* Neural thinking indicator — shown while waiting for first token */}
        {isThinking && !message.content ? (
          <AgentThinkingIndicator />
        ) : (
          <>
            {message.content && (
              <div className={isUser ? 'whitespace-pre-wrap' : 'prose prose-sm prose-slate max-w-none [&>p]:my-1 [&>ul]:my-1 [&>ol]:my-1 [&>h1]:text-base [&>h2]:text-sm [&>h3]:text-xs'}>
                {isUser ? (
                  <p>{message.content}</p>
                ) : (
                  <>
                    <SimpleMarkdown text={message.content} />
                    {showCursor && <StreamingCursor />}
                  </>
                )}
              </div>
            )}
            {!isUser && <CardRenderer cards={message.cards} onApplyProducts={onApplyProducts} />}
          </>
        )}
      </div>
    </div>
  );
}

function SimpleMarkdown({ text }: { text: string }) {
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    const hMatch = line.match(/^(#{1,3})\s+(.+)/);
    if (hMatch) {
      const level = hMatch[1].length;
      const Tag = `h${level}` as keyof JSX.IntrinsicElements;
      elements.push(<Tag key={i}><InlineFormatted text={hMatch[2]} /></Tag>);
      continue;
    }

    if (line.match(/^\s*[-*]\s+/)) {
      const content = line.replace(/^\s*[-*]\s+/, '');
      elements.push(
        <div key={i} className="flex gap-1.5 ml-2">
          <span className="text-slate-400 shrink-0">•</span>
          <span><InlineFormatted text={content} /></span>
        </div>,
      );
      continue;
    }

    const numMatch = line.match(/^\s*(\d+)\.\s+(.+)/);
    if (numMatch) {
      elements.push(
        <div key={i} className="flex gap-1.5 ml-2">
          <span className="text-slate-400 shrink-0 font-mono text-[10px]">{numMatch[1]}.</span>
          <span><InlineFormatted text={numMatch[2]} /></span>
        </div>,
      );
      continue;
    }

    if (!line.trim()) {
      elements.push(<div key={i} className="h-1" />);
      continue;
    }

    elements.push(<p key={i}><InlineFormatted text={line} /></p>);
  }

  return <>{elements}</>;
}

function InlineFormatted({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={i}>{part.slice(2, -2)}</strong>;
        }
        if (part.startsWith('*') && part.endsWith('*')) {
          return <em key={i}>{part.slice(1, -1)}</em>;
        }
        return <span key={i}>{part}</span>;
      })}
    </>
  );
}
